import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getHourlyProfit, HourlyProfitBreakdown } from '../../api/profit';
import './HourlyProfitHeatmap.css';

interface HourlyProfitHeatmapProps {
  selectedDate?: string;
  productId?: string;
}

const HourlyProfitHeatmap: React.FC<HourlyProfitHeatmapProps> = ({ selectedDate, productId }) => {
  // Get last 7 days of hourly data
  const getDates = () => {
    const dates = [];
    const today = selectedDate || new Date().toISOString().split('T')[0];
    const [year, month, dayOfMonth] = today.split('-').map(Number);
    const baseDate = new Date(year, month - 1, dayOfMonth);

    for (let i = 6; i >= 0; i--) {
      const date = new Date(baseDate);
      date.setDate(date.getDate() - i);
      const dateStr = date.getFullYear() + '-' +
        String(date.getMonth() + 1).padStart(2, '0') + '-' +
        String(date.getDate()).padStart(2, '0');
      dates.push(dateStr);
    }
    return dates;
  };

  const dates = getDates();

  // Fetch hourly profit data for the last 7 days
  const queries = dates.map(date =>
    useQuery({
      queryKey: ['hourly-profit', date, productId],
      queryFn: () => getHourlyProfit(date, productId),
      staleTime: 60000,
      retry: 1,
    })
  );

  const isLoading = queries.some(q => q.isLoading);
  const hasError = queries.some(q => q.isError);

  if (isLoading) {
    return (
      <div className="profit-heatmap-container">
        <div className="profit-heatmap-loading">Loading hourly profit data...</div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="profit-heatmap-container">
        <div className="profit-heatmap-error">Unable to load hourly profit data</div>
      </div>
    );
  }

  // Process hourly data into a 7x24 grid
  const hourlyData: { [dateKey: string]: { [hour: number]: number } } = {};
  let minProfit = 0;
  let maxProfit = 0;
  let hasAnyData = false;

  dates.forEach((date, dayIndex) => {
    hourlyData[date] = {};
    const query = queries[dayIndex];

    if (query.data && Array.isArray(query.data)) {
      query.data.forEach((breakdown: HourlyProfitBreakdown) => {
        const profit = breakdown.net_profit || 0;
        hourlyData[date][breakdown.trade_hour] = profit;

        if (profit !== 0) {
          hasAnyData = true;
          minProfit = Math.min(minProfit, profit);
          maxProfit = Math.max(maxProfit, profit);
        }
      });
    }
  });

  // Calculate color intensity based on profit
  const getColor = (profit: number) => {
    if (profit === 0 || !hasAnyData) return '#1e293b';

    const maxAbsProfit = Math.max(Math.abs(minProfit), Math.abs(maxProfit));

    if (profit < 0) {
      const intensity = Math.min(Math.abs(profit) / maxAbsProfit, 1);
      const red = Math.round(254 - (254 - 185) * intensity);
      const green = Math.round(226 - (226 - 28) * intensity);
      const blue = Math.round(226 - (226 - 28) * intensity);
      return `rgb(${red}, ${green}, ${blue})`;
    } else {
      const intensity = Math.min(profit / maxAbsProfit, 1);
      const red = Math.round(209 - (209 - 22) * intensity);
      const green = Math.round(250 - (250 - 163) * intensity);
      const blue = Math.round(229 - (229 - 74) * intensity);
      return `rgb(${red}, ${green}, ${blue})`;
    }
  };

  const formatHour = (hour: number) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}${period}`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="profit-heatmap-container">
      <table className="profit-heatmap-table">
        <thead>
          <tr>
            <th className="profit-corner-cell"></th>
            {dates.map((date) => (
              <th key={date} className="profit-date-header">
                {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 24 }, (_, hour) => (
            <tr key={hour}>
              <td className="profit-hour-label">{formatHour(hour)}</td>
              {dates.map((date) => {
                const profit = hourlyData[date]?.[hour] || 0;
                const hasTradeData = hourlyData[date]?.[hour] !== undefined;
                return (
                  <td
                    key={date}
                    className="profit-cell"
                    style={{ backgroundColor: getColor(profit) }}
                    title={`${formatHour(hour)} on ${new Date(date + 'T00:00:00').toLocaleDateString()}: ${hasTradeData ? formatCurrency(profit) : 'No trades'}`}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {hasAnyData && (
        <div className="profit-heatmap-legend">
          <div className="profit-legend-item">
            <div className="profit-legend-color profit-legend-loss"></div>
            <span>Loss</span>
          </div>
          <div className="profit-legend-item">
            <div className="profit-legend-color profit-legend-neutral"></div>
            <span>No Activity</span>
          </div>
          <div className="profit-legend-item">
            <div className="profit-legend-color profit-legend-profit"></div>
            <span>Profit</span>
          </div>
        </div>
      )}

      {!hasAnyData && (
        <div className="profit-heatmap-empty">No hourly profit data available for the selected period</div>
      )}
    </div>
  );
};

export default HourlyProfitHeatmap;

